import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import App from './App.vue'
import router from './router'
import { useMainStore } from './store'
import axios from 'axios'
import * as date_filters from './filters/date_filters'
import hljs from 'highlight.js/lib/core'
import javascript from 'highlight.js/lib/languages/javascript'
import 'highlight.js/styles/github.css'

// Register highlight.js languages
hljs.registerLanguage('javascript', javascript)

// Suppress ResizeObserver errors during layout changes.
// Must be set up before any other code runs
const debounce = (callback: Function, delay: number) => {
  let tid: number
  return function (...args: any[]) {
    const ctx = self
    tid && clearTimeout(tid)
    tid = window.setTimeout(() => {
      callback.apply(ctx, args)
    }, delay)
  }
}

const _ = window.ResizeObserver
window.ResizeObserver = class ResizeObserver extends _ {
  constructor(callback: ResizeObserverCallback) {
    callback = debounce(callback, 20)
    super(callback)
  }
}

// Also catch any errors that slip through
const originalErrorHandler = window.onerror
window.onerror = function (message, source, lineno, colno, error) {
  if (
    typeof message === 'string' &&
    message.includes('ResizeObserver loop')
  ) {
    return true // Suppress the error
  }
  if (originalErrorHandler) {
    return originalErrorHandler(message, source, lineno, colno, error)
  }
  return false
}

const app = createApp(App)

// Initialize Pinia
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
app.use(pinia)

// Make date filters available globally
app.config.globalProperties.$filters = {
  dateTimeToDateString: date_filters.dateTimeToDateString,
  dateTimeToDateWithTimeString: date_filters.dateTimeToDateWithTimeString,
}

// Настройка axios для отправки cookies (необходимо для CSRF)
axios.defaults.withCredentials = true

// Функция для получения CSRF токена из cookies
function getCsrfToken(): string | null {
  const name = 'csrftoken'
  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const trimmed = cookie.trim()
    if (trimmed.startsWith(name + '=')) {
      return trimmed.substring(name.length + 1)
    }
  }
  return null
}

// Настройка axios interceptors для добавления токена авторизации
axios.interceptors.request.use(
  (request) => {
    // Добавляем CSRF токен для небезопасных методов (POST, PUT, PATCH, DELETE)
    const unsafeMethods = ['post', 'put', 'patch', 'delete']
    if (request.method && unsafeMethods.includes(request.method.toLowerCase())) {
      const csrfToken = getCsrfToken()
      if (csrfToken) {
        request.headers['X-CSRFToken'] = csrfToken
      }
    }

    // Пропускаем добавление токена для запросов авторизации
    if (
      request.url &&
      (request.url.includes('/api/auth/login/') || request.url.includes('/api/auth/refresh/'))
    ) {
      return request
    }

    // Функция для получения токена из различных источников
    const getToken = (): string | null => {
      let token: string | null = null

      // 1. Пробуем получить из Pinia store
      try {
        const store = useMainStore()
        if (store && store.token) {
          token = store.token
          // Проверяем, что токен валидный
          if (
            token &&
            typeof token === 'string' &&
            token.trim() !== '' &&
            token !== 'undefined' &&
            token !== 'null'
          ) {
            return token
          }
        }
      } catch (e) {
        // Игнорируем ошибки чтения из store
      }

      // 2. Пробуем получить из localStorage напрямую (сохраняется в Default.vue)
      try {
        token = localStorage.getItem('token')
        if (
          token &&
          typeof token === 'string' &&
          token.trim() !== '' &&
          token !== 'undefined' &&
          token !== 'null'
        ) {
          return token
        }
      } catch (e) {
        // Игнорируем ошибки чтения из localStorage
      }

      // 3. Пробуем получить из pinia-persist storage
      try {
        const persistedState = localStorage.getItem('logus-saas')
        if (persistedState) {
          const parsed = JSON.parse(persistedState)
          if (
            parsed &&
            parsed.token &&
            typeof parsed.token === 'string' &&
            parsed.token.trim() !== '' &&
            parsed.token !== 'undefined' &&
            parsed.token !== 'null'
          ) {
            return parsed.token
          }
        }
      } catch (e) {
        // Игнорируем ошибки чтения из pinia-persist
      }

      return null
    }

    const token = getToken()

    // Добавляем заголовок только если токен валидный
    if (token) {
      request.headers.Authorization = 'Bearer ' + token
    }

    return request
  },
  (error) => Promise.reject(error)
)

// Настройка axios interceptors для обработки ошибок авторизации
axios.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    if (error.response && error.response.status === 401 && !['login', 'login-oidc-callback'].includes(router.currentRoute.value.name as string)) {
      const store = useMainStore()
      store.logout()
      let query = {
        next: router.currentRoute.value.fullPath,
      }
      if (router.currentRoute.value.fullPath == '/login') {
        query = { next: '' }
      }
      router.push({
        name: 'login',
        query: query,
      })
    }
    return Promise.reject(error)
  }
)

app.use(router)

app.mount('#app')
